import { useEffect, useState, useCallback } from 'react'
import { leadService, type PendingDiscount, type DiscountLimits } from '../../leads/services/leads'
import { authService } from '../../identity/services/auth'
import { UserRole } from '../../../types'
import { usePendingDiscounts } from '../../../contexts/PendingDiscountsContext'
import { CheckCircle, XCircle, ArrowUpCircle, Clock, AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react'
import { useWindowWidth } from '../../../lib/useWindowWidth'

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(isoStr: string): { label: string; urgent: boolean; critical: boolean } {
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000 / 3600
  const h    = Math.floor(diff)
  const m    = Math.round((diff - h) * 60)
  const label = h > 0 ? `${h}h ${m}m ago` : `${m}m ago`
  return { label, urgent: diff >= 4, critical: diff >= 8 }
}

function timeColor(age: ReturnType<typeof timeAgo>) {
  if (age.critical) return 'var(--danger-text)'
  if (age.urgent)   return 'var(--warning-text)'
  return 'var(--text-tertiary)'
}

// ── Escalation chain viewer ───────────────────────────────────────────────────

function EscalationChain({ chain }: { chain: Array<{ role: string; by: string; comment: string; at: string }> }) {
  if (!chain || chain.length === 0) return null
  return (
    <ol className="space-y-1 mt-2 pl-3" style={{ borderLeft: '2px solid var(--border-default)' }}>
      {chain.map((step, i) => (
        <li key={i} className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
          <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>
            {step.role.replace(/_/g, ' ')}
          </span>
          {' '}by {step.by}
          {step.comment && <span> — "{step.comment}"</span>}
          <span className="ml-1">· {timeAgo(step.at).label}</span>
        </li>
      ))}
    </ol>
  )
}

// ── Single discount row ───────────────────────────────────────────────────────

interface RowProps {
  d: PendingDiscount
  limits: DiscountLimits
  currentRole: UserRole
  onApprove: (id: number) => Promise<void>
  onReject:  (id: number, reason: string) => Promise<void>
  onEscalate:(id: number, comment: string) => Promise<void>
  acting: number | null   // call_log id currently being acted on
}

function DiscountRow({ d, limits, currentRole, onApprove, onReject, onEscalate, acting }: RowProps) {
  const [expanded, setExpanded]     = useState(false)
  const [mode, setMode]             = useState<'reject' | 'escalate' | null>(null)
  const [comment, setComment]       = useState('')

  const isPrimary = (currentRole === UserRole.TEAM_LEADER && d.escalated_to === 'TEAM_LEADER') ||
                    (currentRole === UserRole.INSURANCE_HEAD && d.escalated_to === 'INSURANCE_HEAD') ||
                    currentRole === UserRole.DEALER_PRINCIPAL

  // Show escalate only when TL is the designated approver AND discount exceeds TL limit
  const canEscalate = currentRole === UserRole.TEAM_LEADER &&
                      d.escalated_to === 'TEAM_LEADER' &&
                      (d.od_discount_offered ?? 0) > limits.team_leader

  const busy = acting === d.id

  const handleAction = async () => {
    if (mode === 'reject')   await onReject(d.id, comment)
    if (mode === 'escalate') await onEscalate(d.id, comment)
    setMode(null)
    setComment('')
  }

  return (
    <div
      className="card"
      style={{
        borderLeft: `3px solid ${isPrimary ? 'var(--brand-primary)' : 'var(--border-default)'}`,
        padding: '14px 16px',
      }}
    >
      {/* Header row */}
      <div className="flex items-start gap-3 flex-wrap">
        {/* Lead identity */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              {d.vehicle_reg_no || `Lead #${d.lead_id}`}
            </span>
            {d.vehicle_model && (
              <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {d.vehicle_model}
              </span>
            )}
            {/* Level badge */}
            <span
              className={isPrimary ? 'badge-warning' : 'badge-info'}
              style={{ fontSize: 11 }}
            >
              {d.escalated_to === 'TEAM_LEADER' ? 'TL-level' : 'IH-level'}
              {!isPrimary && ' (step-in)'}
            </span>
          </div>

          {/* TC + discount */}
          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
            <span className="font-medium">{d.tc_name ?? 'Telecaller'}</span>
            {' '}offered{' '}
            <span
              className="font-bold tabular-nums"
              style={{
                color: (d.od_discount_offered ?? 0) > limits.insurance_head
                  ? 'var(--danger-text)'
                  : (d.od_discount_offered ?? 0) > limits.team_leader
                    ? 'var(--warning-text)'
                    : 'var(--success-text)',
              }}
            >
              {d.od_discount_offered ?? 0}% OD discount
            </span>
            {d.escalation_comment && (
              <span style={{ color: 'var(--text-tertiary)' }}>
                {' '}— "{d.escalation_comment}"
              </span>
            )}
          </p>
        </div>

        {/* Age */}
        <div className="flex items-center gap-1 shrink-0 text-xs" style={{ color: timeColor({ label: '', urgent: false, critical: false }) }}>
          <Clock className="h-3.5 w-3.5" />
          {d.created_at ? (() => {
            const a = timeAgo(d.created_at)
            return <span style={{ color: timeColor(a) }}>{a.label}</span>
          })() : '—'}
        </div>

        {/* Expand chain toggle */}
        {d.escalation_chain && d.escalation_chain.length > 0 && (
          <button
            className="btn-icon"
            style={{ width: 28, height: 28 }}
            onClick={() => setExpanded(v => !v)}
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>

      {/* Escalation chain */}
      {expanded && d.escalation_chain && (
        <EscalationChain chain={d.escalation_chain} />
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button
          className="btn-primary"
          style={{ height: 30, fontSize: 12, padding: '0 12px' }}
          disabled={busy}
          onClick={() => onApprove(d.id)}
        >
          <CheckCircle className="h-3.5 w-3.5" />
          Approve
        </button>

        {mode === 'reject' ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <input
              className="input flex-1"
              style={{ height: 30, fontSize: 12 }}
              placeholder="Reason for rejection…"
              value={comment}
              onChange={e => setComment(e.target.value)}
              autoFocus
            />
            <button
              className="btn-danger"
              style={{ height: 30, fontSize: 12, padding: '0 10px', whiteSpace: 'nowrap' }}
              disabled={busy}
              onClick={handleAction}
            >
              Confirm Reject
            </button>
            <button className="btn-secondary" style={{ height: 30, fontSize: 12, padding: '0 10px' }} onClick={() => setMode(null)}>
              Cancel
            </button>
          </div>
        ) : mode === 'escalate' ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <input
              className="input flex-1"
              style={{ height: 30, fontSize: 12 }}
              placeholder="Comment for Insurance Head…"
              value={comment}
              onChange={e => setComment(e.target.value)}
              autoFocus
            />
            <button
              className="btn-secondary"
              style={{ height: 30, fontSize: 12, padding: '0 10px', whiteSpace: 'nowrap', color: 'var(--warning-text)', borderColor: 'var(--warning-border)' }}
              disabled={busy}
              onClick={handleAction}
            >
              Confirm Escalate
            </button>
            <button className="btn-secondary" style={{ height: 30, fontSize: 12, padding: '0 10px' }} onClick={() => setMode(null)}>
              Cancel
            </button>
          </div>
        ) : (
          <>
            <button
              className="btn-secondary"
              style={{ height: 30, fontSize: 12, padding: '0 12px', color: 'var(--danger-text)' }}
              disabled={busy}
              onClick={() => { setMode('reject'); setComment('') }}
            >
              <XCircle className="h-3.5 w-3.5" />
              Reject
            </button>

            {canEscalate && (
              <button
                className="btn-secondary"
                style={{ height: 30, fontSize: 12, padding: '0 12px', color: 'var(--warning-text)' }}
                disabled={busy}
                onClick={() => { setMode('escalate'); setComment('') }}
              >
                <ArrowUpCircle className="h-3.5 w-3.5" />
                Escalate to IH
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function DiscountQueuePage() {
  const [items, setItems]     = useState<PendingDiscount[]>([])
  const [limits, setLimits]   = useState<DiscountLimits>({ telecaller: 0, team_leader: 15, insurance_head: 30 })
  const [loading, setLoading] = useState(true)
  const [acting, setActing]   = useState<number | null>(null)
  const [toast, setToast]     = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  void useWindowWidth()
  const currentUser = authService.getCurrentUser()
  const currentRole = currentUser?.role as UserRole
  const { refresh: refreshBadge } = usePendingDiscounts()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [pending, lim] = await Promise.all([
        leadService.getPendingDiscounts(),
        leadService.getDiscountLimits(),
      ])
      setItems(pending)
      setLimits(lim)
    } catch {
      setToast({ type: 'error', text: 'Failed to load pending discounts' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text })
    setTimeout(() => setToast(null), 4000)
  }

  const handleApprove = async (callId: number) => {
    setActing(callId)
    try {
      await leadService.approveDiscount(callId)
      showToast('success', 'Discount approved — TC has been notified.')
      await load(); refreshBadge()
    } catch { showToast('error', 'Failed to approve discount') }
    finally { setActing(null) }
  }

  const handleReject = async (callId: number, reason: string) => {
    setActing(callId)
    try {
      await leadService.rejectDiscount(callId, reason)
      showToast('success', 'Discount rejected — TC has been notified.')
      await load(); refreshBadge()
    } catch { showToast('error', 'Failed to reject discount') }
    finally { setActing(null) }
  }

  const handleEscalate = async (callId: number, comment: string) => {
    setActing(callId)
    try {
      await leadService.escalateDiscount(callId, comment)
      showToast('success', 'Escalated to Insurance Head.')
      await load(); refreshBadge()
    } catch { showToast('error', 'Failed to escalate') }
    finally { setActing(null) }
  }

  // Split into primary (user is designated approver) and step-in (IH covering TL backlog)
  const primary  = items.filter(d =>
    (currentRole === UserRole.TEAM_LEADER    && d.escalated_to === 'TEAM_LEADER') ||
    (currentRole === UserRole.INSURANCE_HEAD && d.escalated_to === 'INSURANCE_HEAD') ||
     currentRole === UserRole.DEALER_PRINCIPAL,
  )
  const stepIn   = currentRole === UserRole.INSURANCE_HEAD
    ? items.filter(d => d.escalated_to === 'TEAM_LEADER')
    : []

  return (
    <div className="h-full flex flex-col gap-4">

      {/* Header */}
      <div className="section-header shrink-0" style={{ marginBottom: 0 }}>
        <div>
          <h1 className="page-title flex items-center gap-2">
            Discount Approvals
            <button className="btn-icon" onClick={load} disabled={loading} title="Refresh" aria-label="Refresh" style={{ marginLeft: 'auto' }}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </h1>
          <p className="page-subtitle">
            {loading ? 'Loading…' : `${items.length} pending approval${items.length !== 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`shrink-0 ${toast.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
          {toast.type === 'success'
            ? <CheckCircle className="h-4 w-4 shrink-0" />
            : <AlertTriangle className="h-4 w-4 shrink-0" />}
          <span>{toast.text}</span>
        </div>
      )}

      {/* Your approval limit */}
      {(() => {
        const myLimit =
          currentRole === UserRole.TEAM_LEADER    ? limits.team_leader :
          currentRole === UserRole.INSURANCE_HEAD ? limits.insurance_head :
          limits.insurance_head   // DP — same as IH ceiling
        return (
          <div
            className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs"
            style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-default)' }}
          >
            <span style={{ color: 'var(--text-tertiary)' }}>Your approval limit:</span>
            <span className="font-semibold tabular-nums" style={{ color: 'var(--brand-primary)' }}>
              up to {myLimit}%
            </span>
          </div>
        )
      })()}

      {/* Queue — scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
        {loading ? (
          <div className="empty-state">
            <div className="spinner" />
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <CheckCircle className="h-10 w-10 mb-3" style={{ color: 'var(--success-text)', opacity: 0.5 }} />
            <p className="font-medium" style={{ color: 'var(--text-secondary)' }}>All clear</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>No pending discount approvals</p>
          </div>
        ) : (
          <>
            {/* Primary approvals */}
            {primary.length > 0 && (
              <div className="space-y-3">
                {currentRole === UserRole.INSURANCE_HEAD && (
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                    Your approvals ({primary.length})
                  </p>
                )}
                {primary.map(d => (
                  <DiscountRow key={d.id} d={d} limits={limits} currentRole={currentRole}
                    onApprove={handleApprove} onReject={handleReject} onEscalate={handleEscalate}
                    acting={acting} />
                ))}
              </div>
            )}

            {/* Step-in backlog (IH covering TL items) */}
            {stepIn.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                  TL backlog — you can step in ({stepIn.length})
                </p>
                {stepIn.map(d => (
                  <DiscountRow key={d.id} d={d} limits={limits} currentRole={currentRole}
                    onApprove={handleApprove} onReject={handleReject} onEscalate={handleEscalate}
                    acting={acting} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
